/**
 * Passkey sign-in for the login form.
 *
 * While the form is mounted, passkey autofill (WebAuthn Conditional UI) is
 * armed on the email field. `signInWithPicker` opens the browser's passkey
 * picker instead; the browser allows one WebAuthn request at a time, so it
 * aborts the pending autofill request first and re-arms it if the picker
 * fails.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import {
  isExpiredPasskeyChallenge,
  isPasskeyCancellation,
  passkeyErrorMessage,
} from '@shared/auth/passkeyErrors';
import { isPasskeyAutofillAvailable, isPasskeySupported } from '@shared/auth/passkeys';

/**
 * The autofill prompt can outlive the server's challenge (5 minutes); a pick
 * after that fails as expired, so autofill re-arms with a fresh challenge —
 * a bounded number of times, in case the server keeps refusing.
 */
const MAX_AUTOFILL_ATTEMPTS = 3;

/**
 * Autofill failures shown to the user: only those after they picked a
 * passkey. Anything earlier (passkeys off, network) happened without any
 * action of theirs, and the password form still works.
 */
const AUTOFILL_REPORTED_CODES = new Set([
  'webauthn_credential_not_found',
  'webauthn_verification_failed',
]);

function shouldReportAutofillError(error: unknown): boolean {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? error.code : undefined;
  return typeof code === 'string' && AUTOFILL_REPORTED_CODES.has(code);
}

interface PasskeySignIn {
  /** The browser supports passkeys; otherwise offer none. */
  supported: boolean;
  /** Open the browser's passkey picker. */
  signInWithPicker: () => Promise<void>;
  isSigningIn: boolean;
  /** The last failure worth showing ('' when none). */
  error: string;
}

export function usePasskeySignIn(): PasskeySignIn {
  const { loginWithPasskey } = useAuth();
  const supported = isPasskeySupported();
  const [error, setError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  /** Bumped to re-arm autofill after the picker was used. */
  const [autofillRun, setAutofillRun] = useState(0);
  const autofillController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!supported) return;
    const controller = new AbortController();
    autofillController.current = controller;

    void (async () => {
      if (!(await isPasskeyAutofillAvailable()) || controller.signal.aborted) return;
      for (let attempt = 1; attempt <= MAX_AUTOFILL_ATTEMPTS; attempt++) {
        try {
          await loginWithPasskey({ conditional: true, signal: controller.signal });
          return;
        } catch (err: unknown) {
          if (controller.signal.aborted || isPasskeyCancellation(err)) return;
          if (isExpiredPasskeyChallenge(err)) continue;
          if (shouldReportAutofillError(err)) setError(passkeyErrorMessage(err) ?? '');
          return;
        }
      }
    })();

    return () => {
      controller.abort();
      if (autofillController.current === controller) autofillController.current = null;
    };
  }, [supported, loginWithPasskey, autofillRun]);

  const signInWithPicker = useCallback(async () => {
    autofillController.current?.abort();
    setError('');
    setIsSigningIn(true);
    try {
      await loginWithPasskey();
    } catch (err: unknown) {
      setError(passkeyErrorMessage(err) ?? '');
      setAutofillRun((run) => run + 1);
    } finally {
      setIsSigningIn(false);
    }
  }, [loginWithPasskey]);

  return { supported, signInWithPicker, isSigningIn, error };
}
